import { Field, FieldRow } from '@/components/ui/field';
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select';
import { Input } from '@chakra-ui/react';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useFormContext, FieldValues, Controller } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import States from './states';
import { Combobox } from './ui/combobox';
import { ZipCodeInput } from './form/zip-code-input';
import { api, AutocompleteResultDto, PlaceDetailsDto } from '@/lib/api';

interface AddressProps {
    isRequired: boolean;
    isReadOnly?: boolean;
    validAddress?: boolean;
    validCity?: boolean;
    validState?: boolean;
    validZipCode?: boolean;
    errorAddress?: string | undefined;
    errorCity?: string | undefined;
    errorState?: string | undefined;
    errorZipCode?: string | undefined;
    nameAddress: string;
    nameCity: string;
    nameState: string;
    nameZipCode: string;
}

export default function Address({
    isRequired,
    isReadOnly,
    validAddress,
    validCity,
    validState,
    validZipCode,
    errorAddress,
    errorCity,
    errorState,
    errorZipCode,
    nameAddress,
    nameCity,
    nameState,
    nameZipCode,
}: AddressProps) {
    const { register, setValue } = useFormContext<FieldValues>();
    const queryClient = useQueryClient();
    const [selectedValue, setSelectedValue] = useState<{ value: string; label: string } | undefined>(undefined);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [autocompleteInput, setAutocompleteInput] = useState('');
    const [selectedPlaceId, setSelectedPlaceId] = useState<string | undefined>(undefined);
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    const { data: fetchedPredictions, isFetching: isFetchingPredictions } = useQuery<AutocompleteResultDto[] | undefined>({
        queryKey: ['googleMaps', 'autocomplete', autocompleteInput],
        queryFn: async () => {
            if (!autocompleteInput || autocompleteInput.length < 3) {
                return [];
            }
            const { data } = await api.googleMaps.autocomplete({ input: autocompleteInput });
            return data;
        },
        enabled: autocompleteInput.length >= 3,
    });

    const { data: placeDetails } = useQuery<PlaceDetailsDto | null>({
        queryKey: ['googleMaps', 'placeDetails', selectedPlaceId],
        queryFn: async () => {
            if (!selectedPlaceId) {
                return null;
            }
            const { data } = await api.googleMaps.placeDetails(selectedPlaceId);
            return data;
        },
        enabled: !!selectedPlaceId,
        staleTime: 5 * 60 * 1000,
    });

    const populateFormFields = useCallback(
        (details: PlaceDetailsDto, placeId: string) => {
            setValue(nameAddress, details.streetAddress, { shouldValidate: true });
            setValue(nameCity, details.city, { shouldValidate: true });
            setValue(nameState, details.state, { shouldValidate: true });
            setValue(nameZipCode, details.zipCode, { shouldValidate: true });
            setSelectedValue({ value: placeId, label: details.streetAddress });
        },
        [setValue, nameAddress, nameCity, nameState, nameZipCode],
    );

    useEffect(() => {
        if (placeDetails && selectedPlaceId) {
            populateFormFields(placeDetails, selectedPlaceId);
        }
    }, [placeDetails, selectedPlaceId, populateFormFields]);

    const handleInputChange = useCallback((value: string) => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        if (!value || value.length < 3) {
            setSelectedValue(undefined);
            setAutocompleteInput('');
            setIsSearching(false);
            return;
        }

        // Indicate a lookup is in progress from the first keystroke through the debounce; once the query
        // fires, isFetchingPredictions takes over.
        setIsSearching(true);
        debounceTimer.current = setTimeout(() => {
            setAutocompleteInput(value);
            setIsSearching(false);
        }, 1000);
    }, []);

    const handlePlaceSelect = useCallback(
        (placeId: string | undefined) => {
            if (!placeId) {
                setSelectedValue(undefined);
                setSelectedPlaceId(undefined);
                return;
            }

            // Check if we have cached data for this place
            const cachedDetails = queryClient.getQueryData<PlaceDetailsDto>(['googleMaps', 'placeDetails', placeId]);
            if (cachedDetails) {
                // Immediately populate form with cached data
                populateFormFields(cachedDetails, placeId);
            }

            setSelectedPlaceId(placeId);
        },
        [queryClient, populateFormFields],
    );

    const items = useMemo(() => fetchedPredictions?.map((p) => ({ value: p.placeId, label: p.description })), [fetchedPredictions]);

    return (
        <>
            <Field label="Add&zwnj;ress" required={isRequired} readOnly={isReadOnly} invalid={validAddress} errorText={errorAddress}>
                <Controller
                    name={nameAddress}
                    render={({ field: { value, onChange, ...props } }) => {
                        // Derive display value from field.value so async reset() calls (e.g. when form data
                        // loads after mount) are reflected without needing a separate sync effect.
                        const effectiveSelectedValue = value && selectedValue?.label !== value ? { value, label: value } : selectedValue;

                        return (
                            <Combobox
                                {...props}
                                name={nameAddress}
                                items={items || []}
                                value={effectiveSelectedValue}
                                onChange={handlePlaceSelect}
                                onValueInput={(e) => {
                                    handleInputChange(e);
                                    onChange(e);
                                }}
                                renderItem={(item) => item.label || value}
                                disabled={isReadOnly}
                                loading={isSearching || isFetchingPredictions}
                                loadingMessage="Searching addresses…"
                                creatable
                                portalled={false}
                            />
                        );
                    }}
                />
            </Field>
            <FieldRow>
                <Field label="City" required={isRequired} readOnly={isReadOnly} invalid={validCity} errorText={errorCity}>
                    <Input type="text" id={nameCity} {...register(nameCity)} />
                </Field>
                <Field label="State" required={isRequired} disabled={isReadOnly} invalid={validState} errorText={errorState}>
                    <NativeSelectRoot variant="outline" id={nameState}>
                        <NativeSelectField {...register(nameState)}>
                            <States />
                        </NativeSelectField>
                    </NativeSelectRoot>
                </Field>
                <Field label="ZIP" required={isRequired} readOnly={isReadOnly} invalid={validZipCode} errorText={errorZipCode}>
                    <Controller name={nameZipCode} render={({ field: { ...props } }) => <ZipCodeInput {...props} />} />
                </Field>
            </FieldRow>
        </>
    );
}
